import { Button } from '@/components/ui/button';
import { UserCog } from 'lucide-react';
import { apiFetch, logout } from '@/lib/api';
import { IMPERSONATOR_NAME_KEY, setSession, useAccessToken } from '@/lib/session';

// Shown whenever a super admin is impersonating another user (detected by the admin's name, which
// admin/Users.tsx leaves in localStorage; their actual session is parked server-side in an httpOnly
// cookie). Anchored to the bottom so it never collides with the pages' sticky top navbar, and stays
// visible while scrolling.
export default function ImpersonationBanner() {
  // Subscribed so the banner goes when the session does.
  const signedIn = !!useAccessToken();
  const impersonating = signedIn && !!localStorage.getItem(IMPERSONATOR_NAME_KEY);
  if (!impersonating) return null;

  const impersonatedName = localStorage.getItem('userName') || 'this user';

  const stop = async () => {
    try {
      // skipAuth: the parked cookie is the credential, and the impersonated token may have lapsed.
      const data = await apiFetch<{ token: string; user: { name: string }; workspaceId?: string | null }>(
        '/auth/stop-impersonating', { method: 'POST', skipAuth: true });
      setSession({ token: data.token, userName: data.user.name, workspaceId: data.workspaceId });
      // Hard reload so all React Query caches / workspace context reset as the admin.
      window.location.href = '/admin/users';
    } catch {
      // The admin's own session lapsed while they were away: both are over now.
      await logout();
      window.location.href = '/login';
    }
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] bg-amber-500 text-amber-950 shadow-lg">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <UserCog className="h-4 w-4 shrink-0" />
          <span>Impersonating <strong>{impersonatedName}</strong> — actions you take affect their account.</span>
        </div>
        <Button size="sm" variant="outline" className="bg-white/80 hover:bg-white border-amber-700/30 text-amber-950 shrink-0" onClick={stop}>
          Return to my account
        </Button>
      </div>
    </div>
  );
}
